import FormManager, { SubmissionState } from 'components/LeadForm/FormManager';
import ModalForm from 'components/LeadForm/ModalForm';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import back from 'public/static/images/vdp/back.svg';
import { titleStyles } from 'components/LeadForm/styles';
import { LeadFormFields } from 'types/LeadForm';
import { colors, screen } from 'components/styles/variables';
import { LeadSubmission } from 'lib/hooks/useSubmitted';
import useOptimizelyDecisions from 'lib/hooks/useOptimizelyDecisions';

interface TitleProps {
  isSubmitted: boolean;
  onClickClose: () => void;
}

function ModalTitle({ isSubmitted, onClickClose }: TitleProps): JSX.Element {
  const { t } = useTranslation();
  const { testLeadCTA } = useOptimizelyDecisions();
  const translationVariant = testLeadCTA ? 'leadForm.requestInfo' : 'leadForm.checkAvailability';
  return (
    <header
      className={classNames('title', 'lead-form-title', {
        contacted: isSubmitted,
      })}
    >
      <button aria-label="close" onClick={onClickClose} className="close-button">
        <img src={back} alt="back" />
      </button>
      <div className="text-container">
        <h3 className="text">
          {isSubmitted ? t('leadForm.sellerContacted') : t(translationVariant)}
        </h3>
        {isSubmitted && <div className="sub-text">{t('leadForm.contactedSubtext')}</div>}
      </div>
      <div className={isSubmitted ? 'contacted-check' : 'plane'} />
      <style jsx>{titleStyles}</style>
      <style jsx>{`
        .close-button {
          border: none;
          padding: 0;
          background-color: transparent;
          margin-right: 10px;
        }

        @media (min-width: 850px) {
          .close-button {
            display: none;
          }
        }

        .text-container {
          flex-grow: 1;
        }

        .title {
          display: flex;
          padding: 14px 16px;
          border-bottom: 1px solid ${colors.G2};
          width: 100%;
          margin-bottom: 0px;
        }

        @media (min-width: ${screen.smMin}) {
          .title {
            border-bottom: none;
            padding: 0px 16px;
          }
        }

        .sub-text {
          display: none;
        }

        @media (min-width: ${screen.smMin}) {
          .sub-text {
            display: block;
          }
        }
      `}</style>
    </header>
  );
}

interface Props {
  showZipCode?: boolean;
  showAddress?: boolean;
  onSubmit: (fields: LeadFormFields) => Promise<void>;
  onClose: () => void;
  isSubmitted?: boolean;
  leadFormClass?: string;
  leadSubmission: LeadSubmission;
}

export default function ModalLeadForm({
  onSubmit,
  onClose,
  leadSubmission,
  showZipCode,
  showAddress,
}: Props): JSX.Element {
  return (
    <FormManager onSubmit={onSubmit}>
      {({ submissionState }: { submissionState: SubmissionState }): JSX.Element => {
        const isSubmitted =
          leadSubmission.isSubmitted || submissionState === SubmissionState.submitted;

        return (
          <>
            <ModalTitle isSubmitted={isSubmitted} onClickClose={onClose} />
            <ModalForm
              submissionState={
                leadSubmission.isSubmitted ? SubmissionState.submitted : submissionState
              }
              isSubmitted={isSubmitted}
              marginClassName="margin"
              showZipCode={showZipCode}
              showAddress={showAddress}
              inputClassName={classNames('padding')}
              leadSubmission={leadSubmission}
            />
            <style jsx>{`
              .padding {
                padding: 0px 16px;
              }
            `}</style>
          </>
        );
      }}
    </FormManager>
  );
}
